import { useEffect, useState } from "react";
import classnames from "classnames";

import SecureField from "./SecureField";
import SecureFieldIcon from "./SecureFieldIcon";

import "./SecureField.css";
import InputField from "../Form/InputField";
import MonthField from "../Form/MonthField";
import Button from "../Button";
import { useTranslation } from "react-i18next";

/**
 *
 * Test data
 * https://docs.datatrans.ch/docs/testing-credentials
 */
const getUrl = (production: boolean) =>
  production
    ? "https://pay.datatrans.com/upp/payment/js/secure-fields-2.0.0.min.js"
    : "https://pay.sandbox.datatrans.com/upp/payment/js/secure-fields-2.0.0.min.js";

const initalInputStyle =
  "font-size: 100%; border-radius: 0; -webkit-appearance: none; padding: 0";
const initalCssClass = {
  "secure-field": true,
  "secure-field__input ": true,
  "secure-field__base ": true,
  "secure-field__has-actions ": true,
  "secure-field__has-card-icon": true,
  "secure-field__has-error": false,
  "secure-field__is-recognized": false,
};

declare global {
  interface Window {
    SecureFields: any;
  }
}

interface ISecureFields {
  production: boolean;
  config: any;
  callback: (creditCardData: any) => void;
}

export default function SecureFields(props: ISecureFields) {
  const { production, config, callback } = props;
  const [secureFields, setSecureFields] = useState<any>(null);
  const [message, setMessage] = useState<any>(null);
  const [messageCC, setMessageCC] = useState<any>(null);
  const [cardIcon, setCardIcon] = useState("card-empty");
  const [cvvIcon, setCvvIcon] = useState("cvv-empty");
  const [cardContainerClassNames, setCardContainerClassNames] =
    useState(initalCssClass);
  const [cvvContainerClassNames, setCvvContainerClassNames] =
    useState(initalCssClass);
  const {t} = useTranslation();

  interface IFormState {
    Name: string;
    ExpirationDate: string;
    TransactionId: number;
  }

  const InitialFormState = {
    Name: "",
    ExpirationDate: "",
    TransactionId: 0,
  };
  const [formState, setFormState] = useState<IFormState>(InitialFormState);
  const initialValidState = {
    Name: null,
    ExpirationDate: null,
    TransactionId: null,
  };
  const [validationState, setValidationState] = useState<{
    [key: string]: boolean | null;
  }>(initialValidState);

  const initSecureFields = () => {
    if (typeof window.SecureFields !== "function") {
      setMessage(t("scenes.payment.somethingWentWrong"));
      return null;
    }

    try {
      const initalizedSecureFields = new window.SecureFields();
      initalizedSecureFields.initTokenize(
        config.merchantID,
        config.fields,
        config.options
      );
      setSecureFields(initalizedSecureFields);
      return initalizedSecureFields;
    } catch {
      setMessage(t("scenes.payment.somethingWentWrong"));
      return null;
    }
  };

  useEffect(() => {
    let instance: { destroy?: () => void } | null = null;
    let cancelled = false;
    const scriptSource = getUrl(production);

    const fail = () => {
      if (!cancelled) {
        setMessage(t("scenes.payment.somethingWentWrong"));
      }
    };

    const start = () => {
      if (cancelled) return;
      instance = initSecureFields();
    };

    const timeoutId = window.setTimeout(fail, 10000);

    const onLoad = () => {
      window.clearTimeout(timeoutId);
      start();
    };
    const onError = () => {
      window.clearTimeout(timeoutId);
      fail();
    };

    if (typeof window.SecureFields === "function") {
      window.clearTimeout(timeoutId);
      start();
    } else {
      const existing = document.querySelector(
        `script[src="${scriptSource}"]`,
      ) as HTMLScriptElement | null;

      if (existing) {
        existing.addEventListener("load", onLoad);
        existing.addEventListener("error", onError);
      } else {
        const script = document.createElement("script");
        script.src = scriptSource;
        script.async = true;
        script.onload = onLoad;
        script.onerror = onError;
        document.body.appendChild(script);
      }
    }

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      const existing = document.querySelector(`script[src="${scriptSource}"]`);
      existing?.removeEventListener("load", onLoad);
      existing?.removeEventListener("error", onError);
      window.setTimeout(() => {
        try {
          instance?.destroy?.();
        } catch {
          // Datatrans can throw if the iframe is already gone
        }
      }, 1);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (secureFields) {
      secureFields.on("ready", () => {
        // Set styles manually as they're inside an iframe and out of the scope of the parent's stylesheets
        secureFields.setStyle("cardNumber", initalInputStyle);
        secureFields.setStyle("cvv", initalInputStyle);
        // secureFields.focus("cardNumber");
      });

      // Set class names and icon when fields change
      secureFields.on("change", (data: any) => {
        let paymentMethod = data.fields.cardNumber.paymentMethod
          ? data.fields.cardNumber.paymentMethod
          : false;

        setMessage(null);
        setMessageCC(null);
        setCardContainerClassNames({
          ...cardContainerClassNames,
          "secure-field__is-recognized": !!paymentMethod,
          "secure-field__has-error": false,
        });
        setCvvContainerClassNames({
          ...cvvContainerClassNames,
          "secure-field__has-error": false,
        });
        setCardIcon(paymentMethod ? "brands/" + paymentMethod : "card-empty");
        setCvvIcon("cvc-empty");
      });

      // Set error icon and class name on validate failure
      secureFields.on("validate", (data: any) => {
        setMessageCC(null);
        if (
          data.fields.cardNumber.paymentMethod !== "VIS" &&
          data.fields.cardNumber.paymentMethod !== "ECA"
        ) {
          setCardContainerClassNames({
            ...cardContainerClassNames,
            "secure-field__is-recognized": false,
            "secure-field__has-error": true,
          });
          setCardIcon("card-error");
          setMessageCC(t("scenes.payment.onlyVisaAndMastercardSupported"));
        }

        if (!data.fields.cardNumber.valid) {
          setCardContainerClassNames({
            ...cardContainerClassNames,
            "secure-field__is-recognized": false,
            "secure-field__has-error": true,
          });
          setCardIcon("card-error");
        }

        if (!data.fields.cvv.valid) {
          setCvvContainerClassNames({
            ...cvvContainerClassNames,
            "secure-field__has-error": true,
          });
          setCvvIcon("cvc-error");
        }

        if (data.hasErrors) {
          setValidationState((previousState) => ({
            ...previousState,
            ...{ TransactionId: false },
          }));
          setFormState((previousState) => ({
            ...previousState,
            ...{ TransactionId: 0 },
          }));
        }

        validateFields();
      });

      // Show transaction ID on success or transaction error message
      secureFields.on("success", (data: any) => {
        let message = null;
        if (data.transactionId) {
          setValidationState((previousState) => ({
            ...previousState,
            ...{ TransactionId: true },
          }));
          setFormState((previousState) => ({
            ...previousState,
            ...{ TransactionId: data.transactionId },
          }));

          message = "";
          // (
          //   <pre className={"form-result success"} style={{ marginTop: "20px", fontSize: "1rem" }}>
          //     Data submitted successfully with transaction # {data.transactionId}
          //   </pre>
          // );
        } else if (data.error) {
          setValidationState((previousState) => ({
            ...previousState,
            ...{ TransactionId: false },
          }));
          message = (
            <pre
              className={"form-result " + data.result}
              style={{ marginTop: "20px", fontSize: "1rem" }}
            >
              {data.error}
            </pre>
          );
        }
        setMessage(message);
      });
      secureFields.on("error", function (data: any) {
        if (data && data?.error !== "form submit already ongoing") {
          setValidationState((previousState) => ({
            ...previousState,
            ...{ TransactionId: false },
          }));
          setCardIcon("card-error");
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secureFields]);

  const handleValueCallback = (obj: { [key: string]: string | boolean }) => {
    setFormState((previousState) => ({ ...previousState, ...obj }));
  };

  const handleValidateCallBack = (obj: { [key: string]: boolean | null }) => {
    setValidationState((previousState) => ({ ...previousState, ...obj }));
  };

  const validateFields = () => {
    let isValid = true;

    let obj: { [key: string]: boolean | null } = {};
    ["Name", "ExpirationDate"].forEach((name) => {
      if (validationState[name] === null) {
        obj[name as keyof typeof obj] = false;
        isValid = false;
      } else if (validationState[name] === false) {
        isValid = false;
      }
    });

    if (!isValid) {
      setValidationState((previousState) => ({ ...previousState, ...obj }));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <InputField
        type="text"
        label={t("scenes.payment.nameOnCreditCard")}
        name="Name"
        value={formState.Name}
        error={validationState.Name}
        validate={{ min: 1 }}
        errorMessages={{
          min: t("scenes.payment.nameIsRequired"),
        }}
        valueCallback={handleValueCallback}
        validationCallBack={handleValidateCallBack}
      />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          secureFields?.submit();
        }}
        className="c-form"
      >
        <SecureField
          fieldType="card"
          label={t("scenes.payment.cardNumber")}
          customClass={classnames(cardContainerClassNames)}
          callback={() => secureFields?.focus("cardNumber")}
        >
          <SecureFieldIcon fieldType="card" iconType={cardIcon} />
        </SecureField>

        <div className="mt-4 text-red-600 text-fluid-sm">{messageCC}</div>
        <div className="mt-4"></div>
        <div className="grid grid-cols-2 gap-4">
          <MonthField
            type="tel"
            label={t("scenes.payment.expirationDate")}
            name="ExpirationDate"
            value={formState.ExpirationDate}
            error={validationState.ExpirationDate}
            validate={{ min: 1, expire: true }}
            errorMessages={{
              min: t("scenes.payment.expirationDateRequired"),
              yearMin: t("scenes.payment.yearCanNotBeInThePast"),
              yearMax: t("scenes.payment.yearCanNotBeMoreThan5YearsInTheFuture"),
              monthMin: t("scenes.payment.monthCanNotBeInThePast"),
              monthMax: t("scenes.payment.monthCanNotBeBiggerThan12"),
              expire: t("scenes.payment.shouldBeAMonthAndAYear"),
            }}
            valueCallback={handleValueCallback}
            validationCallBack={handleValidateCallBack}
          />
          <SecureField
            fieldType="cvv"
            label="CVV"
            customClass={classnames(cvvContainerClassNames)}
            callback={() => secureFields?.focus("cvv")}
          >
            <SecureFieldIcon fieldType="cvv" iconType={cvvIcon} />
          </SecureField>
        </div>

        {validationState.Name === true &&
        validationState.ExpirationDate === true &&
        validationState.TransactionId === true ? (
          <div className="mt-4">
            <Button
              text={t("scenes.payment.createReservation")}
              type="button"
              onClick={() => callback(formState)}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-4 mt-4">
            <Button
              id="form-submit"
              text={t("scenes.payment.validateCard")}
              type="submit"
              classes="secondary"
              onClick={() => {
                secureFields?.submit();
              }}
              disabled={
                validationState.Name === true &&
                validationState.ExpirationDate === true &&
                validationState.TransactionId === true
              }
            />
            {message}
          </div>
        )}
      </form>
    </div>
  );
}
